import type { WavespeedModelConfig } from '../types';
import { clampTo, hasImageInput } from '../../tools';

export const kreaConfigs: Record<string, WavespeedModelConfig> = {
    'wavespeed-ai/krea-v2-large/text-to-image': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/krea-v2-large/text-to-image
        apiPath: '/wavespeed-ai/krea-v2-large/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    prompt: prompt,
                    size: clampTo(aspectRatio || '16:9', ['16:9', '9:16','4:5', '4:3', '3:2', '2:3', '2.35:1', '1:1'] as const, '16:9'),
                    // 图片 URL
                    reference: image_urls.slice(0, 10).map((u: string) => ({ image_url: u, strength: 1 })),
                    // 可选参数
                    creativity: clampTo(providerOptions?.creativity || 'medium', 
                        ['raw', 'low', 'medium', 'high'], 
                        'medium'
                    ),
                };
            } else {
                return {
                    prompt: prompt,
                    size: clampTo(aspectRatio || '16:9', ['16:9', '9:16','4:5', '4:3', '3:2', '2:3', '2.35:1', '1:1'] as const, '16:9'),
                    // 可选参数
                    creativity: clampTo(providerOptions?.creativity || 'medium', 
                        ['raw', 'low', 'medium', 'high'], 
                        'medium'
                    ),
                };
            }
        },
    },
    'wavespeed-ai/krea-v2-medium/text-to-image': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/krea-v2-medium/text-to-image
        apiPath: '/wavespeed-ai/krea-v2-medium/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    prompt: prompt,
                    size: clampTo(aspectRatio || '16:9', ['16:9', '9:16','4:5', '4:3', '3:2', '2:3', '2.35:1', '1:1'] as const, '16:9'),
                    // 图片 URL
                    reference: image_urls.slice(0, 10).map((u: string) => ({ image_url: u, strength: 1 })),
                    // 可选参数
                    creativity: clampTo(providerOptions?.creativity || 'medium',
                        ['raw', 'low', 'medium', 'high'],
                        'medium'
                    ),
                };
            } else {
                return {
                    prompt: prompt,
                    size: clampTo(aspectRatio || '16:9', ['16:9', '9:16','4:5', '4:3', '3:2', '2:3', '2.35:1', '1:1'] as const, '16:9'),
                    // 可选参数
                    creativity: clampTo(providerOptions?.creativity || 'medium',
                        ['raw', 'low', 'medium', 'high'],
                        'medium'
                    ),
                };
            }
        },
    },
    'wavespeed-ai/krea-v2-medium-turbo/text-to-image': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/krea-v2-medium-turbo/text-to-image
        apiPath: '/wavespeed-ai/krea-v2-medium-turbo/text-to-image',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16','4:5', '4:3', '3:2', '2:3', '2.35:1', '1:1'] as const, '16:9'),
                    // 图片 URL
                    reference: image_urls.slice(0, 10).map((u: string) => ({ image_url: u, strength: 1 })),
                    // 可选参数
                    creativity: clampTo(providerOptions?.creativity || 'medium',
                        ['raw', 'low', 'medium', 'high'],
                        'medium'
                    ),
                };
            } else {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16','4:5', '4:3', '3:2', '2:3', '2.35:1', '1:1'] as const, '16:9'),
                    // 可选参数
                    creativity: clampTo(providerOptions?.creativity || 'medium',
                        ['raw', 'low', 'medium', 'high'],
                        'medium'
                    ),
                };
            }
        },
    },
    'wavespeed-ai/krea-v2/turbo': {
        // https://wavespeed.ai/zh-CN/models/wavespeed-ai/krea-v2/turbo
        apiPath: '/wavespeed-ai/krea-v2/turbo',
        pollApiPath: '/predictions/${requestId}/result',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, image_urls, aspectRatio, image_resolution, seed, providerOptions } = params;
            const hasImage = hasImageInput(image_urls);
            if (hasImage) {
                return {
                    prompt: prompt,
                    aspect_ratio: aspectRatio === 'match'
                        ? undefined
                        : clampTo(aspectRatio || '16:9', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '16:9'),
                    resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                    seed: typeof seed === 'number' ? seed : (typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined),
                    // 图片 URL
                    image: image_urls[0],
                    // 可选参数
                    strength: typeof providerOptions?.strength === 'number'
                        ? Math.min(1, Math.max(0, providerOptions.strength))
                        : 0.8,
                };
            } else {
                return {
                    prompt: prompt,
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['1:1', '1:2', '2:1', '1:3', '3:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '9:21', '21:9'] as const, '16:9'),
                    resolution: clampTo(image_resolution?.toLowerCase() || '1k', ['1k', '2k'] as const, '1k'),
                    seed: typeof seed === 'number' ? seed : (typeof providerOptions?.seed === 'number' ? providerOptions.seed : undefined),
                };
            }
        },
    },
};